import { useCreateFeedback } from "@/api/hooks/useCreateFeedback"
import { useCreateUserFeedback } from "@/api/hooks/useUserCreateFeedback"
import { useAuthContext } from "@/components/core/Auth/AuthProvider"
import Button from "@/components/shared/Button"
import { Rating } from "@/components/shared/inputs/Rating"
import { inputStylingProps } from "@/components/shared/inputs/utils"
import Modal from "@/components/shared/Modal"
import Typography from "@/components/shared/Typography"
import { useFeedbackModal } from "@/hooks/useFeedbackModal"
import { useEffect } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { Trans, useTranslation } from "react-i18next"
import { toast } from "react-toastify"
import { InputField } from "rtu-components"

enum FormKeys {
  RATING = "rating",
  COMMENT = "comment",
}
interface FormData {
  [FormKeys.RATING]: string
  [FormKeys.COMMENT]: string
}

const DEFAULT_VALUES: FormData = { [FormKeys.RATING]: "", [FormKeys.COMMENT]: "" }

export const FeedbackModal = () => {
  const form = useForm<FormData>({ defaultValues: DEFAULT_VALUES, mode: "onBlur" })
  const { t } = useTranslation(["common"])
  const { user } = useAuthContext()
  const { mutate: createFeedback } = useCreateFeedback()
  const { mutate: createUserFeedback } = useCreateUserFeedback()

  const { isModalOpen, shouldShowModal, showModal, handleFeedbackSubmitted, handleModalDismissed } = useFeedbackModal()

  // Show modal when conditions are met
  useEffect(() => {
    if (shouldShowModal) {
      showModal()
    }
  }, [shouldShowModal, showModal])

  const onSubmit = (data: FormData) => {
    const rating = Number(data[FormKeys.RATING])
    const comment = data[FormKeys.COMMENT]

    const feedbackData = {
      rating,
      comment: comment || undefined,
      appVersion: "1.0.0",
    }

    const feedbackCallback = {
      onSuccess: () => {
        toast.success(t("feedback.success"))
        // Track the feedback submission in our hook
        handleFeedbackSubmitted(rating, comment)
      },
      onError: () => {
        toast.error(t("feedback.error"))
      },
    }

    if (data[FormKeys.RATING]) {
      if (user?.email) {
        createUserFeedback(feedbackData, feedbackCallback)
      } else {
        createFeedback(feedbackData, feedbackCallback)
      }
    }
  }

  return (
    <Modal isVisible={isModalOpen} setIsVisible={handleModalDismissed} closeOnOverlayClick={false}>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-10">
          <div className="flex flex-col gap-2">
            <Typography type="h2" className="text-primary-700">
              {t("feedback.title")}
            </Typography>
            <Typography type="l" className="text-primary-700">
              <Trans
                i18nKey="feedback.subtitle"
                t={t}
                components={{
                  strong: <strong className="font-bold" />,
                }}
              />
            </Typography>
          </div>
          <Rating name={FormKeys.RATING} />
          {form.watch(FormKeys.RATING) === "5" ? (
            <Typography type="l" className="text-primary-700">
              <Trans
                i18nKey="feedback.rating.two"
                t={t}
                components={{
                  strong: <strong className="font-bold" />,
                }}
              />
            </Typography>
          ) : (
            form.watch(FormKeys.RATING) && (
              <Typography type="l" className="text-primary-700">
                <Trans
                  i18nKey="feedback.rating.one"
                  t={t}
                  components={{
                    strong: <strong className="font-bold" />,
                  }}
                />
              </Typography>
            )
          )}
          <InputField
            {...inputStylingProps}
            name={FormKeys.COMMENT}
            renderLabel={() => (
              <Typography type="caption" className="text-primary-700">
                {t("feedback.comment.label")}
              </Typography>
            )}
            placeholder={t("feedback.comment.placeholder")}
            containerClassName="border-primary-400 focus-within:border-primary-400 bg-transparent text-primary-50"
            inputClassName="border-primary-400 focus:border-primary-400 bg-transparent text-primary-50 placeholder:text-primary-300 text-primary-300"
          />
          <div className="flex gap-3">
            <Button className="flex-1" disabled={!form.watch(FormKeys.RATING)} type="submit">
              {t("feedback.submit")}
            </Button>
          </div>
        </form>
      </FormProvider>
    </Modal>
  )
}
